/**
 * ThreadHeaderActionButtons — bell + pin + move-to + archive for the ACTIVE
 * thread in the chat header's top-right action rail (sidebar/header UX round
 * 3, 2026-08-01; bell 2026-08-26; folder 2026-09-09).
 *
 * Invariant HeaderIconButtons, styled exactly like their rail siblings
 * (add-people / mode-cycle / hide):
 *   • BELL — this viewer's per-thread notification mode; the control itself
 *     is ThreadNotifyBellButton (extracted 2026-09-15 so the COMPACT rail can
 *     mount the same bell inline while the rest of this cluster folds into ⋯).
 *   • PIN — toggles the active thread's pinned state through the SAME
 *     `threadStore.togglePinThread` the row/chip pin affordances call (one
 *     mutation path; pins persist on the thread index doc and sync across
 *     devices). Filled thumbtack + aria-pressed while pinned.
 *   • ARCHIVE — requests archive of the active thread through the SAME
 *     confirm/delete flow the row × uses (the host routes it via
 *     `shouldRouteCloseToEmptyDelete` → one-click empty delete or the
 *     ConfirmationModal → `removeThreadAndReturnFallback`, which lands the
 *     user on the fallback thread). Hidden while the thread is the last one
 *     (`canArchive` — the row ×'s own `canDelete` rule).
 *
 * Presentational: no thread-store access, no routing decisions — the host
 * owns both, so this rail can never diverge from the row affordances.
 * `data-header-pin-btn` / `data-header-archive-btn` are the stable hooks.
 */
import React from 'react';
import { HeaderIconButton } from './styles';
import { PinSvgIcon, ArchiveBoxSvgIcon } from './components';
import type { ThreadNotifyMode } from '../../services/threadNotifications';
import { CategoryMoveButton, type CategoryMoveProps } from './CategoryMoveMenu';
import { ThreadNotifyBellButton } from './ThreadNotifyBellButton';
import { OS_NOTIFICATIONS_BLOCKED_HINT } from './threadNotifyPresentation';

// The hint's home is threadNotifyPresentation.ts (shared with the ⋯ menu's
// bell item since 2026-09-15); re-exported here so existing importers keep
// working unchanged.
export { OS_NOTIFICATIONS_BLOCKED_HINT };

export interface ThreadHeaderActionButtonsProps {
  /** Active thread id — both callbacks receive it. */
  threadId: string;
  /** Display name for aria-labels (routine name / thread title). */
  threadTitle: string;
  /** Current pinned state of the active thread. */
  pinned: boolean;
  /** The SAME togglePinThread the row/chip pin buttons call. */
  onTogglePin: (threadId: string) => void;
  /** Row-×-parity: false (last remaining thread) hides the archive button. */
  canArchive: boolean;
  /** Host routes into the row ×'s confirm/empty-delete flow. */
  onRequestArchive: (threadId: string) => void;
  /**
   * This VIEWER's notification mode for the active thread (owner directive
   * 2026-08-26) — absent hides the bell (host still resolving).
   */
  notifyMode?: ThreadNotifyMode;
  /** Advance the three-state cycle: chime → chime+platform → off → chime. */
  onCycleNotifyMode?: (threadId: string) => void;
  /**
   * Notification.permission === 'denied' for this site (2026-09-08): the bell
   * tooltip says so — the platform state cannot deliver until the user
   * un-blocks it. Never disables the control.
   */
  osNotificationsBlocked?: boolean;
  /**
   * Per-thread badges (2026-09-08): OTHER threads carry unseen activity —
   * never the open one. Renders a small dot on the bell.
   */
  otherThreadsUnseen?: boolean;
  /**
   * PERSONAL ORGANIZATION (PR-5, 2026-09-09): the folder button — file this
   * thread under one of the viewer's categories. Absent → no button.
   */
  moveTo?: CategoryMoveProps;
}

export const ThreadHeaderActionButtons: React.FC<ThreadHeaderActionButtonsProps> = ({
  threadId,
  threadTitle,
  pinned,
  onTogglePin,
  canArchive,
  onRequestArchive,
  notifyMode,
  onCycleNotifyMode,
  osNotificationsBlocked,
  otherThreadsUnseen,
  moveTo,
}) => {
  return (
  <>
    {notifyMode && onCycleNotifyMode && (
      <ThreadNotifyBellButton
        threadId={threadId}
        threadTitle={threadTitle}
        notifyMode={notifyMode}
        onCycleNotifyMode={onCycleNotifyMode}
        osNotificationsBlocked={osNotificationsBlocked}
        otherThreadsUnseen={otherThreadsUnseen}
      />
    )}
    <HeaderIconButton
      data-header-pin-btn=""
      data-pinned={pinned ? 'true' : 'false'}
      aria-pressed={pinned}
      aria-label={pinned ? `unpin thread ${threadTitle}` : `pin thread ${threadTitle} to the top`}
      title={pinned ? 'unpin thread' : 'pin thread to top'}
      onClick={() => onTogglePin(threadId)}
    >
      <PinSvgIcon filled={pinned} />
    </HeaderIconButton>
    {moveTo && <CategoryMoveButton threadTitle={threadTitle} {...moveTo} />}
    {canArchive && (
      <HeaderIconButton
        data-header-archive-btn=""
        aria-label={`archive thread ${threadTitle} (Prest0n keeps it in memory for reference; the conversation closes)`}
        title="archive thread"
        onClick={() => onRequestArchive(threadId)}
      >
        <ArchiveBoxSvgIcon />
      </HeaderIconButton>
    )}
  </>
  );
};

export default ThreadHeaderActionButtons;
